import { addMonths, format, startOfDay, subDays } from 'date-fns'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { DateInput } from '@/components/ui/DateInput'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { formatDate, parseDateOnly } from '../../../../../lib/formatters'

interface BindDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (dateStart: string | undefined) => Promise<void>
  /** The start date the carrier priced (YYYY-MM-DD), offered as the default. */
  pricedStartDate?: string | null
}

export function BindDialog({ open, onOpenChange, onConfirm, pricedStartDate }: BindDialogProps) {
  const pricedStart = parseDateOnly(pricedStartDate)
  // The picker hides past days, so a priced date that has passed is shown, not preselected.
  const [dateStart, setDateStart] = useState<Date | undefined>(
    pricedStart && pricedStart >= startOfDay(new Date()) ? pricedStart : undefined
  )
  const [loading, setLoading] = useState(false)

  const handleConfirm = async () => {
    setLoading(true)
    try {
      await onConfirm(dateStart ? format(dateStart, 'yyyy-MM-dd') : undefined)
      setDateStart(undefined)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    onOpenChange(false)
    setDateStart(undefined)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Bind coverage</DialogTitle>
          <DialogDescription>
            Are you sure you want to bind this coverage? This will create an insurance policy.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Label>Policy start date (optional)</Label>
          <DateInput
            label="Select date"
            value={dateStart}
            onChange={setDateStart}
            hidden={[{ before: startOfDay(new Date()) }, { after: subDays(addMonths(new Date(), 3), 1) }]}
          />
          {pricedStartDate && (
            <p className="text-muted-foreground text-sm">
              The carrier priced this quote to start {formatDate(pricedStartDate)}.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={loading} loading={loading ? 'Loading\u2026' : undefined}>
            Bind
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
